import { Controller } from '@hotwired/stimulus';

const ONE_HOUR_MS = 60 * 60 * 1000;
const ONE_DAY_MS = 24 * ONE_HOUR_MS;
const ONE_WEEK_MS = 7 * ONE_DAY_MS;
const TICK_INTERVAL_MS = 30 * 1000;

const BASE = 'votes--dashboard--next-event-component';
const DANGER_CLASS = `${BASE}__countdown--danger`;
const CHIP_DANGER_CLASS = `${BASE}--danger`;
const CHIP_LABEL_SELECTOR = '.toolkit-button-component__toolbar-chip-label';

export default class extends Controller {
  static targets = ['item', 'countdown', 'overflow', 'chip', 'dialog', 'list', 'empty'];

  declare readonly itemTargets: HTMLElement[];
  declare readonly countdownTargets: HTMLElement[];
  declare readonly overflowTarget: HTMLElement;
  declare readonly hasOverflowTarget: boolean;
  declare readonly chipTarget: HTMLButtonElement;
  declare readonly dialogTarget: HTMLElement;
  declare readonly listTarget: HTMLElement;
  declare readonly emptyTarget: HTMLElement;

  private intervalId: number | null = null;

  connect(): void {
    this.tick();
    this.intervalId = window.setInterval(() => this.tick(), TICK_INTERVAL_MS);
  }

  disconnect(): void {
    if (this.intervalId !== null) {
      window.clearInterval(this.intervalId);
      this.intervalId = null;
    }
    // Turbo snapshots the DOM after disconnect; closing first keeps an open dialog from being
    // cached and flashing back on a return visit.
    this.closeDialog();
  }

  openDialog(): void {
    this.tick();
    this.dialogTarget.setAttribute('open', 'open');
    this.chipTarget.setAttribute('aria-pressed', 'true');
  }

  closeDialog(): void {
    this.dialogTarget.removeAttribute('open');
    this.chipTarget.setAttribute('aria-pressed', 'false');
  }

  backdropClick(event: MouseEvent): void {
    if (event.target === this.dialogTarget) this.closeDialog();
  }

  private tick(): void {
    const live = this.itemTargets.filter((item) => this.render(item));

    this.renderChip(live.length);
    this.setHidden(this.listTarget, live.length === 0);
    this.setHidden(this.emptyTarget, live.length > 0);

    if (this.hasOverflowTarget) {
      this.setHidden(this.overflowTarget, live.length === 0);
    }

    // Nothing left to count down, but the chip stays: it is the toolbar's answer to "what is due",
    // and an answer of none is still an answer.
    if (live.length === 0 && this.intervalId !== null) {
      window.clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  // The count is the chip's whole job when it is closed, so it tracks the rows that are still
  // live rather than the number the page was rendered with.
  private renderChip(liveCount: number): void {
    const label = this.chipTarget.querySelector<HTMLElement>(CHIP_LABEL_SELECTOR);
    if (label) label.textContent = `Upcoming (${liveCount})`;

    const urgent = this.countdownTargets.some((countdown) =>
      countdown.classList.contains(DANGER_CLASS)
    );
    this.element.classList.toggle(CHIP_DANGER_CLASS, urgent);
  }

  // Returns false once an event has run out or drifted past the horizon, which retires its row.
  private render(item: HTMLElement): boolean {
    const target = Date.parse(item.getAttribute(`data-${this.identifier}-at-param`) ?? '');
    if (Number.isNaN(target)) {
      this.setHidden(item, true);
      return false;
    }

    const remainingMs = target - Date.now();
    if (remainingMs <= 0 || remainingMs > ONE_WEEK_MS) {
      this.setHidden(item, true);
      return false;
    }

    const countdown = item.querySelector<HTMLElement>(`[data-${this.identifier}-target="countdown"]`);
    if (countdown) {
      countdown.textContent = `${this.verb(item)} in ${this.formatDuration(remainingMs)}`;
      countdown.classList.toggle(DANGER_CLASS, remainingMs < ONE_HOUR_MS);
    }

    return true;
  }

  private setHidden(element: HTMLElement, hidden: boolean): void {
    if (hidden) {
      element.setAttribute('hidden', 'hidden');
    } else {
      element.removeAttribute('hidden');
    }
  }

  private verb(item: HTMLElement): string {
    const kind = item.getAttribute(`data-${this.identifier}-kind-param`);
    return kind === 'ends' ? 'ends' : 'starts';
  }

  private formatDuration(ms: number): string {
    if (ms >= ONE_DAY_MS) {
      const days = Math.floor(ms / ONE_DAY_MS);
      const hours = Math.floor((ms % ONE_DAY_MS) / ONE_HOUR_MS);
      return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
    }

    const totalMinutes = Math.max(1, Math.floor(ms / 60000));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    if (hours <= 0) {
      return `${minutes}m`;
    }

    return `${hours}h ${minutes}m`;
  }
}
